/**
 * Logger helper function
 *
 * @packageDocumentation
 */

/* eslint-disable
    @typescript-eslint/no-empty-function,
    @typescript-eslint/explicit-module-boundary-types,
    @typescript-eslint/no-explicit-any,
    no-console
*/
export type LogLevelType = 'debug' | 'error' | 'info' | 'log' | 'trace' | 'warn'

export const UrlSearchParamDebugKey = 'debug'
export const UrlSearchParamDebugLoggingValue = 'logging'
export const UrlSearchParamSourceKey = 'source'
export const UrlSearchParamSourceValue = 'dcs'
export const LOGGER_CONSOLE = Object.assign({}, window.console)

/**
 *  Timestamp styling
 */
const TIMESTAMP_BACKGROUND_COLOUR = '#0C4B33'
const TIMESTAMP_TEXT_COLOUR = '#FFFFFF'

const MAX_TABLE_SIZE = 10

const disableConsole = () => {
  console.error = () => {}
  console.debug = () => {}
  console.log = () => {}
  console.time = () => {}
  console.timeEnd = () => {}
  console.timeStamp = () => {}
  console.trace = () => {}
  console.warn = () => {}
}

/**
 * Check if URL has query param ?logger=debugging to begin logging
 * also check for DCS only messaging &source=dcs
 */
export const loggerEnabled = (): boolean => {
  const params = new URL(String(window.location.href)).searchParams
  if (
    params.has(UrlSearchParamDebugKey) &&
    UrlSearchParamDebugLoggingValue === params.get(UrlSearchParamDebugKey) &&
    params.get(UrlSearchParamSourceKey) &&
    UrlSearchParamSourceValue === params.get(UrlSearchParamSourceKey)
  ) {
    disableConsole()
    return true
  } else {
    return params.has(UrlSearchParamDebugKey) && UrlSearchParamDebugLoggingValue === params.get(UrlSearchParamDebugKey)
  }
}

/**
 * Add a timestamp to each log.
 * @returns array of string with a timestamp and background-color formatting
 */
export const addTimeStamp = () => {
  return [
    `%c[${(Date.now() - performance.timeOrigin).toFixed()}ms]`,
    `background-color:${TIMESTAMP_BACKGROUND_COLOUR}; color: ${TIMESTAMP_TEXT_COLOUR}`
  ]
}

/**
 * Default logger. Logs arrays and objects as tables with console.table()
 * Logs all other args with the specified console level
 * Objects and arrays should be passed as separate params, NOT with string literals to avoid [object Object] logs
 * @param logLevel: Log level
 * @param args: Arguments to log
 */
export const logger = (logLevel: LogLevelType, ...args: any[]) => {
  if (loggerEnabled()) {
    for (const arg of args) {
      if (
        (typeof arg === 'object' && Object.keys(arg).length <= MAX_TABLE_SIZE) ||
        (arg.constructor === 'Array' && arg.length <= MAX_TABLE_SIZE)
      ) {
        LOGGER_CONSOLE.table(arg)
      } else {
        LOGGER_CONSOLE[logLevel](...addTimeStamp(), arg)
      }
    }
  }
}

/**
 * Start a new timer. Up to 10,000 timers can be running at once.
 * @param timerLabel: Label for timer. Used to reference in loggerTimerLog() and loggerTimerEnd() functions
 */
export const loggerTimer = (timerLabel: string) => {
  if (loggerEnabled()) {
    LOGGER_CONSOLE.log(...addTimeStamp(), `Starting new timer with label: ${timerLabel}`)
    LOGGER_CONSOLE.time(timerLabel)
  }
}

/**
 * End an existing timer.
 * @param timerLabel
 */
export const loggerTimerEnd = (timerLabel: string) => {
  if (loggerEnabled()) {
    LOGGER_CONSOLE.timeEnd(timerLabel)
    LOGGER_CONSOLE.log(...addTimeStamp(), `Timer ended with label: ${timerLabel}`)
  }
}

/**
 * Log the current time of an existing timer.
 * @param timerLabel
 */
export const loggerTimeLog = (timerLabel: string) => {
  if (loggerEnabled()) {
    LOGGER_CONSOLE.timeLog(timerLabel)
  }
}

/**
 * Log to the console within a group.
 * Can be used to add a label to group certain related logs together
 * @param groupLabel: label of the group
 * @param logLevel: log level type of the log message
 * @param args: args to log
 */
export const loggerGroup = (groupLabel: string, logLevel: LogLevelType, ...args: any[]) => {
  if (loggerEnabled()) {
    LOGGER_CONSOLE.group(groupLabel)
    logger(logLevel, ...args)
    LOGGER_CONSOLE.groupEnd()
  }
}
